import { useMemo, useState } from 'react';
import { t } from '@hana/i18n';
import '../strings.i18n';
import { IconArrowLeft, IconBell, IconChartBar, IconDownload, IconPlus } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Drawer, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { useWide } from '../useWide';
import { CATS, CAT_TONE, CAT_VI, DEVS, NO_FILTER, RANGES, TOTAL, USERS, baseRows, countBy, initials, matches, type Filter, type LogRow, type Range } from './actionlog';
import s from './actionlog.module.css';

// Mint-ActionLog (1536): nhật ký thao tác của mọi người. Dữ liệu mẫu; chưa đọc DB.

const KINDS = [['user', 'Người', USERS], ['cat', 'Loại', CATS], ['dev', 'Thiết bị', DEVS], ['hour', 'Buổi', ['Sáng', 'Chiều']]] as const;
type Kind = (typeof KINDS)[number][0];
const KIND_LBL: Record<Kind, string> = { user: 'Người', cat: 'Loại', dev: 'Thiết bị', hour: 'Buổi' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const CAT_C: Record<string, string> = { AUTH: 'var(--ok)', NAV: 'var(--pri)', JOB: 'var(--link)', SI: 'var(--warn)', SERVICE: 'var(--err)', EMAIL: 'var(--pri)', PRINT: 'var(--lb)', ADMIN: 'var(--warn)' };
const DEV_C = ['var(--pri)', 'var(--link)', 'var(--warn)', 'var(--ok)', 'var(--lb)'];

function Bars({ label, items, kind, fl, toggle, donut }: { label: string; items: [string, number][]; kind: Kind; fl: Filter; toggle: (k: Kind, v: string) => void; donut?: boolean }) {
  const mx = Math.max(1, ...items.map((x) => x[1]));
  const colorOf = (k: string) => (kind === 'cat' ? CAT_C[k] : kind === 'dev' ? DEV_C[(DEVS as readonly string[]).indexOf(k)] : undefined) ?? 'var(--pri)';
  const tot = items.reduce((a, x) => a + x[1], 0) || 1;
  let acc = 0;
  return (
    <div className={s.chart}>
      <b>{label}</b>
      {donut && (
        <svg role="img" aria-label={`${label}: ${items.length} loại`} width="78" height="78" viewBox="0 0 132 132" style={{ float: 'left', marginRight: 10 }}>
          <circle cx="66" cy="66" r="52" fill="none" stroke="var(--ctl)" strokeWidth="16" />
          {items.map(([k, n]) => { const L = (n / tot) * 326.7; const o = acc; acc += L; return <circle key={k} cx="66" cy="66" r="52" fill="none" stroke={colorOf(k)} strokeWidth="16" strokeDasharray={`${Math.max(0, L - 3)} ${326.7 - L + 3}`} strokeDashoffset={-o} transform="rotate(-90 66 66)" />; })}
          <text x="66" y="74" textAnchor="middle" fontSize="24" fontWeight="550" fill="var(--tx)">{items.length}</text>
        </svg>
      )}
      <div role="group" aria-label={`${label}: ${items.map(([k, n]) => `${k} ${n}`).join(', ')}`} className={s.bars}>
        {items.map(([k, n]) => (
          <button key={k} type="button" className={s.barB} aria-pressed={fl[kind].includes(k)} title={`Lọc ${k}`} onClick={() => toggle(kind, k)}>
            <span>{k}</span><span className={s.track}><span className={s.fill} style={{ width: `${Math.round((n / mx) * 100)}%`, background: colorOf(k) }} /></span><span className={s.num}>{n}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ActionLog() {
  const toast = useToast();
  const nav = useNavigate();
  const wide = useWide();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const [range, setRange] = useState<Range>('week');
  const [q, setQ] = useState('');
  const [fl, setFl] = useState<Filter>(NO_FILTER);
  const [dense, setDense] = useState<'std' | 'tight'>('std');
  const [stats, setStats] = useState(true);
  const [fOpen, setFOpen] = useState(false);
  const [fKind, setFKind] = useState<Kind>('user');
  const [draft, setDraft] = useState<Filter>(NO_FILTER);
  const [sel, setSel] = useState<string | null>(null);

  const base = useMemo(() => (state === 'empty' ? [] : baseRows(range, q)), [range, q, state]);
  const list = useMemo(() => base.filter((r) => matches(r, fl)), [base, fl]);
  const say = (m: string) => toast({ msg: m });
  const toggle = (k: Kind, v: string) => setFl((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [v] }));
  const chips = KINDS.flatMap(([k]) => fl[k].map((o) => ({ k, o })));
  const kindVals = KINDS.find((k) => k[0] === fKind)![2] as readonly string[];
  const cur: LogRow | undefined = list.find((r) => r.id === sel);
  const rowH = dense === 'tight' ? 40 : 48;
  const users6 = countBy(list, 'user', USERS).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const devs = countBy(list, 'dev', DEVS);
  const catN = countBy(list, 'cat', CATS).filter((p) => p[1] > 0);
  const rangeK = { today: 'hôm nay', week: '7 ngày', month: '30 ngày' }[range];
  const kpis = [
    ['Lượt thao tác', TOTAL[range].toLocaleString('vi-VN'), rangeK],
    ['Người dùng', String(new Set(list.map((r) => r.user)).size), `trong ${list.length} dòng khớp`],
    ['Thiết bị', String(new Set(list.map((r) => r.dev)).size), 'loại thiết bị'],
    ['Quản trị (ADMIN)', String(list.filter((r) => r.cat === 'ADMIN').length), 'xem như · sửa dữ liệu · M365'],
  ] as const;
  const openFilter = () => { setDraft(fl); setFOpen((o) => !o); };

  const table = (
    <>
      <div role="table" aria-label="Nhật ký thao tác" className={s.table}>
        <div role="row" className={`${s.row} ${s.head}`}>
          {['Lúc', 'Người', 'Loại', 'Thao tác', 'Chi tiết', 'Thiết bị'].map((h) => <span key={h} role="columnheader">{h}</span>)}
        </div>
        {list.map((r) => (
          <div key={r.id} role="row" tabIndex={0} aria-selected={sel === r.id} className={`${s.row} ${s.body}`} style={{ height: rowH }} onClick={() => setSel(r.id)} onKeyDown={(e) => { if (e.key === 'Enter') setSel(r.id); }}>
            <span role="cell" className={`${s.cell} ${s.rd}`} style={{ fontSize: 'inherit' }}>{r.when}</span>
            <span role="cell" className={s.who}><span aria-hidden="true" className={s.av}>{initials(r.user)}</span><span className={s.cell} style={{ fontWeight: 'var(--fw-label)' }}>{r.user}</span></span>
            <span role="cell"><span className={`${s.tag} ${s[CAT_TONE[r.cat]]}`}>{r.cat}</span></span>
            <span role="cell" className={s.cell} style={{ fontWeight: 500 }}>{r.act}</span>
            <span role="cell" className={s.cell}>{r.detail}</span>
            <span role="cell" className={s.cell}>{r.dev}</span>
          </div>
        ))}
        {list.length === 0 && (
          <StateView state="empty" title={base.length === 0 && !q ? 'Chưa có thao tác nào' : 'Không có dòng khớp'} desc={base.length === 0 && !q ? 'Thao tác mới sẽ hiện ở đây.' : 'Thử bỏ bớt bộ lọc hoặc đổi khoảng ngày.'}
            action={chips.length || q ? <Button onClick={() => { setFl(NO_FILTER); setQ(''); }}>{t('kit.x.boHetLoc')}</Button> : undefined}>
            <span />
          </StateView>
        )}
      </div>
      <div className={s.foot}><span>{list.length} dòng</span></div>
    </>
  );

  return (
    <Page>
      <PageHeader
        title="Action Log"
        lead={<button type="button" aria-label="Về Quản trị hệ thống" title="Về Quản trị hệ thống" style={{ width: 36, height: 36, border: 0, borderRadius: 11, background: 'var(--card)', boxShadow: 'var(--sh)', color: 'var(--tx2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => void nav({ to: '/kit' } as never)}><IconArrowLeft size={17} aria-hidden="true" /></button>}
        actions={
          <>
            <HeaderTool label="Thông báo · 2 mới" icon={IconBell} n={2} onClick={() => say('Thông báo chưa mở ở bản này')} />
            <div className={s.grp} role="group" aria-label="Mật độ">
              {([['std', 'Chuẩn'], ['tight', 'Gọn']] as const).map(([k, l]) => <button key={k} type="button" aria-pressed={dense === k} onClick={() => setDense(k)} style={{ height: 32, padding: '0 10px' }}>{l}</button>)}
            </div>
            {!wide && <HeaderTool label="Hiện / ẩn thống kê" icon={IconChartBar} active={stats} onClick={() => setStats((o) => !o)} />}
            <Button variant="primary" icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => say(`Đã xuất ${list.length} dòng ra CSV (bản mẫu)`)}>{t('kit.x.xuatCsv')}</Button>
          </>
        }
      />
      <StateView state={state === 'empty' ? 'ready' : state} title={state === 'noperm' ? 'Action Log cần quyền form ActionLog' : state === 'error' ? 'Không đọc được nhật ký' : undefined}
        desc={state === 'noperm' ? 'Không có quyền xem nhật ký của mọi người.' : state === 'error' ? 'Không tải được nhật ký lúc 08:41. Thao tác mới vẫn được ghi.' : undefined}
        action={state === 'error' ? <Button onClick={() => location.reload()}>{t('kit.x.thuLai')}</Button> : state === 'noperm' ? <Button variant="primary" onClick={() => say('Đã gửi yêu cầu quyền ActionLog')}>{t('kit.x.xinQuyen')}</Button> : undefined} skeleton={<Skeleton rows={10} />}>
        <section aria-label="Nhật ký thao tác" className={s.card}>
          <div className={s.top}>
            <div className={s.bar}>
              <label><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{t('kit.x.timTrongNhat')}</span>
                <input className={s.q} value={q} placeholder="Tìm người, thao tác, mã job, chi tiết…" onChange={(e) => setQ(e.target.value)} /></label>
              <div className={s.grp} role="group" aria-label="Khoảng ngày">
                {RANGES.map(([k, l]) => <button key={k} type="button" aria-pressed={range === k} onClick={() => setRange(k)}>{l}</button>)}
              </div>
              {chips.map(({ k, o }) => <span key={`${k}${o}`} className={s.chipX}>{KIND_LBL[k]}: {o}<button type="button" aria-label={`Bỏ lọc ${o}`} onClick={() => toggle(k, o)}>×</button></span>)}
              <div className={s.add}>
                <button type="button" className={s.addB} aria-expanded={fOpen} onClick={openFilter}><IconPlus size={16} aria-hidden="true" />{t('kit.x.loc')}</button>
                {fOpen && (
                  <>
                    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 15 }} onClick={() => setFOpen(false)} />
                    <div role="dialog" aria-label="Thêm bộ lọc" className={s.pop}>
                      <div className={s.popBody}>
                        <div role="tablist" aria-label="Kiểu lọc" className={s.kinds}>{KINDS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={fKind === k} onClick={() => setFKind(k)}>{l}</button>)}</div>
                        <div className={s.opts}>
                          {kindVals.map((o) => {
                            const n = base.filter((r) => matches(r, { ...draft, [fKind]: [o] })).length;
                            return <label key={o} className={s.opt}><input type="checkbox" checked={draft[fKind].includes(o)} onChange={() => setDraft((d) => ({ ...d, [fKind]: d[fKind].includes(o) ? d[fKind].filter((x) => x !== o) : [...d[fKind], o] }))} /><span>{fKind === 'cat' ? `${o} · ${CAT_VI[o as keyof typeof CAT_VI]}` : o}</span><span className={s.rd}>{n}</span></label>;
                          })}
                        </div>
                      </div>
                      <div className={s.popFoot}>
                        <span className={s.rd}>{base.filter((r) => matches(r, draft)).length} dòng khớp</span>
                        <div style={{ display: 'flex', gap: 6 }}><Button variant="ghost" onClick={() => setDraft(NO_FILTER)}>{t('kit.x.xoa')}</Button><Button variant="primary" onClick={() => { setFl(draft); setFOpen(false); }}>{t('kit.x.apDung')}</Button></div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <span className={s.cnt}>{list.length} / {base.length} dòng mẫu · tổng {TOTAL[range].toLocaleString('vi-VN')} lượt</span>
            </div>
            <div className={s.strip}>
              {kpis.map(([k, v, sub]) => <div key={k} className={s.kpi}><div className={s.rd}>{k}</div><div style={k.startsWith('Quản trị') ? { color: 'var(--warn)' } : undefined}>{v}</div><div className={s.rd}>{sub}</div></div>)}
            </div>
            {stats && !wide && (
              <div className={s.strip}>
                <Bars label="Theo loại" items={catN} kind="cat" fl={fl} toggle={toggle} />
                <Bars label="Theo người · top 6" items={users6} kind="user" fl={fl} toggle={toggle} />
                <Bars label="Theo thiết bị" items={devs.filter((p) => p[1] > 0)} kind="dev" fl={fl} toggle={toggle} donut />
                <div className={s.chart}><b>{t('kit.x.theoGioTrong')}</b>
                  <svg role="img" aria-label="Theo giờ trong ngày" width="100%" height="84" viewBox="0 0 300 110">
                    <line x1="8" y1="88" x2="292" y2="88" stroke="var(--bd)" />
                    <path d={`${hourLine(list)} L286 88 L14 88 Z`} fill="var(--pri)" opacity="0.08" />
                    <path d={hourLine(list)} fill="none" stroke="var(--pri)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            )}
          </div>
          {table}
        </section>
      </StateView>
      <Drawer open={!!cur} onOpenChange={(o) => { if (!o) setSel(null); }} title={cur?.act ?? ''} description={cur ? `${cur.user} · ${cur.when}` : undefined}>
        {cur && (
          <dl className={s.dwB}>
            <dt>{t('kit.x.loai')}</dt><dd>{cur.cat} · {CAT_VI[cur.cat]}</dd><dt>{t('kit.x.nguoi')}</dt><dd>{cur.user}</dd><dt>{t('kit.x.thietBi')}</dt><dd>{cur.dev}</dd><dt>{t('kit.x.chiTiet')}</dt><dd className={s.mono}>{cur.detail}</dd>
          </dl>
        )}
      </Drawer>
    </Page>
  );
}

/** Đường xu hướng theo giờ 07–18 (khung 300×110 như board). */
function hourLine(rows: readonly LogRow[]): string {
  const v = Array.from({ length: 12 }, (_, i) => rows.filter((r) => r.hour === i + 7).length);
  const mx = Math.max(1, ...v);
  return v.map((n, i) => `${i ? 'L' : 'M'}${(14 + (i * 272) / 11).toFixed(1)} ${(88 - (n / mx) * 70).toFixed(1)}`).join(' ');
}
